import {
  Box,
  Card,
  CardContent,
  CardContentProps,
  CardHeader,
  CardProps,
  Tooltip,
  lighten,
  useTheme,
} from '@mui/material';
import TouchRipple from '@mui/material/ButtonBase/TouchRipple';
import { useRef } from 'react';

type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

interface MetricCardProps {
  children: React.ReactNode;
  title: string;
  subheader?: string;
  avatar?: JSX.Element;
  action?: JSX.Element | JSX.Element[];
  cardContentProps?: CardContentProps;
  cardContainerProps?: CardProps & DataAttributes;
  disabled?: boolean;
  onClickFn?: () => void;
  onHeaderClick?: () => void;
  tooltipDescription?: string;
  allowTwoRowHeader?: boolean;
  centerContentX?: boolean;
  centerContentY?: boolean;
}

const WidgetCard = ({
  children,
  title,
  subheader,
  avatar,
  cardContentProps,
  cardContainerProps,
  disabled,
  onClickFn,
  onHeaderClick,
  action,
  tooltipDescription,
  allowTwoRowHeader,
  centerContentX,
  centerContentY,
}: MetricCardProps) => {
  const theme = useTheme();

  const rippleRef = useRef<any>(null);

  const handleMouseDown = (event: any) => {
    rippleRef.current.start(event);
  };

  const handleMouseUp = () => {
    rippleRef.current.stop();
  };

  return (
    <Tooltip title={tooltipDescription ?? undefined} placement='top'>
      <Card
        component='div'
        {...cardContainerProps}
        {...(onClickFn !== undefined &&
          !disabled && { onClick: () => onClickFn() })}
        {...(onClickFn !== undefined &&
          !disabled && { onMouseDown: handleMouseDown })}
        {...(onClickFn !== undefined &&
          !disabled && { onMouseUp: handleMouseUp })}
        {...(onClickFn !== undefined &&
          !disabled && { onTouchStart: handleMouseDown })}
        {...(onClickFn !== undefined &&
          !disabled && { onTouchEnd: handleMouseUp })}
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          border: 'none',
          borderRadius: theme.tokens.borderRadius.lg,
          boxShadow: theme.tokens.semanticShadows.card,
          flexGrow: 1,
          ...cardContainerProps?.sx,
          cursor: onClickFn !== undefined && !disabled ? 'pointer' : 'auto',
          transition:
            onClickFn !== undefined && !disabled
              ? 'all 0.3s ease-in-out'
              : undefined,
          '&:hover':
            onClickFn !== undefined && !disabled
              ? {
                  boxShadow: theme.tokens.semanticShadows.cardHover,
                  transform: 'translateY(-4px)',
                }
              : {},
        }}
      >
        <CardHeader
          {...(action !== undefined && { action: action })}
          avatar={
            <Box
              sx={{
                padding: `${theme.tokens.spacing.sm}px`,
                height: `${theme.tokens.spacing.sm * 5}px`,
                width: `${theme.tokens.spacing.sm * 5}px`,
                mx: `-${theme.tokens.spacing.sm}px`,
                borderRadius: theme.tokens.borderRadius.full,
                background: theme.tokens.colors.card,
                boxShadow: theme.tokens.shadows.sm,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
              }}
            >
              {avatar}
            </Box>
          }
          title={title}
          subheader={subheader}
          subheaderTypographyProps={{
            sx: {
              color: theme.tokens.colors.mutedForeground,
              fontSize: theme.tokens.typography.fontSize.xs,
              fontWeight: theme.tokens.typography.fontWeight.medium,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          titleTypographyProps={{
            variant: 'body1',
            fontSize: `${theme.tokens.typography.fontSize.md} !important`,
            color: theme.palette.primary.main,
            fontWeight: theme.tokens.typography.fontWeight.semibold,
          }}
          sx={{
            px: 3,
            py: 1.5,
            cursor:
              onHeaderClick == null && onClickFn == null ? 'auto' : 'pointer',
            background: lighten(theme.palette.primary.main, 0.95),
            '& .MuiCardHeader-content': {
              minWidth: 0,
              flex: '1 1 auto',
              overflow: 'hidden',
            },
            '& .MuiCardHeader-action': {
              flex: '0 0 auto',
              alignSelf: 'center',
              marginTop: 0,
              marginRight: 0,
              height: 'auto',
              gap: 0.5,
            },
            flexWrap: allowTwoRowHeader ? 'wrap' : 'nowrap',
            gap: 1,
            rowGap: 2,
          }}
          onClick={onHeaderClick}
        />
        <CardContent
          sx={{
            py: 0,
            flex: 1,
            ...(centerContentX || centerContentY
              ? {
                  display: 'flex',
                  alignItems: centerContentX ? 'center' : undefined,
                  justifyContent: centerContentY ? 'center' : undefined,
                }
              : {}),
            ...cardContentProps?.sx,
          }}
        >
          {children}
        </CardContent>
        {onClickFn !== undefined && (
          <TouchRipple ref={rippleRef} center={false} />
        )}
      </Card>
    </Tooltip>
  );
};

export default WidgetCard;
